var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { SecondaryGrid } from "@ui/components/grid";
import { SButton } from "@ui/components/buttons/SButton";
import { useTranslation } from "react-i18next";
import { DailyReportCard } from "./components/DailyReportCard";
import { apReportsApi } from "@api/enpoints/ap-reports-api";
const PAGE_SIZE = 20;
export const DailyReport = () => {
    const { t } = useTranslation("common");
    const { apId } = useParams();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [downloadingId, setDownloadingId] = useState(null);
    const fetchReports = useCallback((pageNumber) => __awaiter(void 0, void 0, void 0, function* () {
        if (!apId)
            return;
        setLoading(true);
        try {
            const result = yield apReportsApi.getApReports(apId, pageNumber, PAGE_SIZE);
            if ((result === null || result === void 0 ? void 0 : result.isSuccess) && result.data) {
                setReports(result.data.items);
                setTotalCount(result.data.totalCount);
            }
        }
        catch (error) {
            console.error("Error fetching AP reports:", error);
        }
        finally {
            setLoading(false);
        }
    }), [apId]);
    useEffect(() => {
        fetchReports(page);
    }, [fetchReports, page]);
    const handleDownload = (reportId) => __awaiter(void 0, void 0, void 0, function* () {
        setDownloadingId(reportId);
        try {
            const blob = yield apReportsApi.downloadApReport(reportId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ap-report-${reportId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error("Error downloading report:", error);
        }
        finally {
            setDownloadingId(null);
        }
    });
    const formatPeriod = (start, end) => {
        const s = new Date(start).toLocaleDateString();
        const e = new Date(end).toLocaleDateString();
        return `${s} – ${e}`;
    };
    const columns = [
        {
            field: "date",
            headerName: t("common:grid.date"),
            flex: 0.7,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => new Date(params.row.date).toLocaleDateString(),
        },
        {
            field: "period",
            headerName: t("common:grid.period"),
            flex: 3,
            minWidth: 320,
            filterable: false,
            renderCell: (params) => formatPeriod(params.row.periodStart, params.row.periodEnd),
        },
        {
            field: "reportingInterval",
            headerName: t("common:grid.interval"),
            flex: 0.6,
            minWidth: 80,
            filterable: false,
        },
        {
            field: "nodeCount",
            headerName: t("common:grid.nodeCount"),
            flex: 0.6,
            minWidth: 80,
            filterable: false,
        },
        {
            field: "alarmCount",
            headerName: t("common:grid.alarms"),
            flex: 0.6,
            minWidth: 80,
            filterable: false,
        },
        {
            field: "actions",
            headerName: "",
            flex: 1,
            minWidth: 200,
            filterable: false,
            type: "string",
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (React.createElement(SButton, { variant: "soft", startIcon: React.createElement(Icon, { icon: "line-md:cloud-down" }), disabled: downloadingId === params.row.id, onClick: () => handleDownload(params.row.id) }, t("common:actions.downloadDayReport"))),
        },
    ];
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.dailyReport"), showBackButton: true }),
        React.createElement(PageContent, null,
            React.createElement(SecondaryGrid, { localeText: { noRowsLabel: t("common:status.noData") }, rows: loading ? [] : reports, columns: columns, loading: loading, checkboxSelection: false, disableColumnMenu: true, disableColumnResize: true, disableColumnSorting: true, renderCard: (row) => (React.createElement(DailyReportCard, { row: row, downloading: downloadingId === row.id, onDownload: handleDownload })), paginationMode: "server", rowCount: totalCount, paginationModel: { page: page - 1, pageSize: PAGE_SIZE }, onPaginationModelChange: (model) => {
                    if (model.page + 1 !== page)
                        setPage(model.page + 1);
                }, pageSizeOptions: [20] }))));
};
